import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldStar, Plus, PencilSimple, Warning, Trash, CalendarBlank, Cpu, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLAN_CATALOG = [
  { key: "care",     label: "CARE",     price: 39, minutes: 30, tone: "text-blue-700 dark:text-blue-400 bg-blue-500/15 border-blue-500/40" },
  { key: "business", label: "BUSINESS", price: 59, minutes: 45, tone: "text-amber-700 dark:text-amber-400 bg-amber-500/15 border-amber-500/40" },
  { key: "complete", label: "COMPLETE", price: 79, minutes: 60, tone: "text-emerald-700 dark:text-emerald-400 bg-emerald-500/15 border-emerald-500/40" },
];

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const fmtDateIT = (iso) => iso ? `${iso.slice(8,10)}/${iso.slice(5,7)}/${iso.slice(0,4)}` : "";

export default function ManagedPlanSection({ customerId }) {
  const [plan, setPlan] = useState(null); // null = none active
  const [loading, setLoading] = useState(true);
  const [activateOpen, setActivateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ plan: "care", pc_count: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/managed-plan`);
      setPlan(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const activate = async () => {
    try {
      await api.post(`/customers/${customerId}/managed-plan`, { plan: form.plan, pc_count: parseInt(form.pc_count, 10) });
      toast.success("Piano attivato");
      setActivateOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/managed-plans/${plan.id}`, { plan: form.plan, pc_count: parseInt(form.pc_count, 10) });
      toast.success(form.plan !== plan.plan ? "Cambio piano programmato dal 1° del mese prossimo" : "Piano aggiornato");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancelScheduled = async () => {
    if (!window.confirm("Annullare il cambio programmato?")) return;
    try {
      // Optimistic update: hide the alert immediately so no visual lag
      setPlan((prev) => prev ? { ...prev, scheduled_change: null } : prev);
      await api.post(`/managed-plans/${plan.id}/cancel-scheduled-change`);
      toast.success("Cambio annullato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load(); // reload to restore state on error
    }
  };

  const deactivate = async () => {
    if (!window.confirm(`Disattivare il piano ${plan.plan_label}? I minuti mensili scompariranno.`)) return;
    try {
      await api.post(`/managed-plans/${plan.id}/deactivate`);
      toast.success("Piano disattivato");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openActivate = () => { setForm({ plan: "care", pc_count: 1 }); setActivateOpen(true); };
  const openEdit = () => { setForm({ plan: plan.plan, pc_count: plan.pc_count }); setEditOpen(true); };

  if (loading) return <Card className="p-4 border-border/70 rounded-lg text-sm text-muted-foreground">Caricamento piano gestito…</Card>;

  const meta = plan ? PLAN_CATALOG.find(p => p.key === plan.plan) : null;

  return (
    <Card className="p-5 border-border/70 rounded-lg" data-testid="managed-plan-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold tracking-tight text-lg flex items-center gap-2">
          <ShieldStar size={20} weight="duotone" className="text-primary" /> Assistenza IT Gestita
        </h2>
        {!plan && (
          <Button size="sm" onClick={openActivate} data-testid="activate-managed-plan-btn" className="rounded-full gap-2">
            <Plus size={14} weight="bold" /> Attiva piano
          </Button>
        )}
      </div>

      {!plan && (
        <div className="text-sm text-muted-foreground italic">
          Nessun piano attivo per questo cliente. Attiva CARE, BUSINESS o COMPLETE per gestire i minuti mensili di assistenza.
        </div>
      )}

      {plan && meta && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`${meta.tone} border font-mono text-xs uppercase`}>{plan.plan_label}</Badge>
            <Badge variant="secondary" className="font-mono text-[11px] gap-1"><Cpu size={11} /> {plan.pc_count} PC</Badge>
            <span className="font-mono text-xs text-muted-foreground">{eur(plan.monthly_price)}/mese · {plan.minutes_per_pc} min/PC</span>
          </div>

          {/* Progress ring / bar for minutes */}
          <div>
            <div className="flex justify-between font-mono text-xs mb-1">
              <span className="text-muted-foreground">Minuti mensili</span>
              <span data-testid="managed-plan-minutes">
                <span className="font-bold text-foreground">{plan.monthly_minutes_remaining}</span>
                <span className="text-muted-foreground"> / {plan.monthly_minutes_total} min disponibili</span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div
                className={`h-full ${plan.monthly_minutes_remaining === 0 ? "bg-destructive" : plan.monthly_minutes_remaining < plan.monthly_minutes_total * 0.2 ? "bg-amber-500" : "bg-emerald-500"}`}
                style={{ width: `${plan.monthly_minutes_total > 0 ? (plan.monthly_minutes_used / plan.monthly_minutes_total) * 100 : 0}%`, transition: "width 0.3s ease" }}
              />
            </div>
            {plan.monthly_minutes_remaining === 0 && (
              <div className="mt-2 text-xs text-destructive flex items-center gap-1"><Warning size={12} weight="fill" />
                Minuti mensili esauriti. Saranno nuovamente disponibili dal 1° del mese prossimo.
              </div>
            )}
          </div>

          {/* Scheduled change alert */}
          {plan.scheduled_change && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs" data-testid="scheduled-change-alert">
              <div className="flex items-start gap-2">
                <CalendarBlank size={14} className="text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-amber-800 dark:text-amber-300">Cambio programmato</div>
                  <div className="text-amber-700 dark:text-amber-400 mt-0.5">
                    Dal <b>{fmtDateIT(plan.scheduled_change.effective_from)}</b> passerai a
                    <b> {(PLAN_CATALOG.find(p => p.key === plan.scheduled_change.plan)?.label) || plan.scheduled_change.plan.toUpperCase()}</b>
                    &nbsp;× {plan.scheduled_change.pc_count} PC. Il piano attuale resta valido fino a fine mese.
                  </div>
                </div>
                <button onClick={cancelScheduled} data-testid="cancel-scheduled-change-btn" className="text-amber-700 dark:text-amber-400 hover:opacity-70" title="Annulla cambio programmato">
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-managed-plan-btn" className="rounded-full gap-1"><PencilSimple size={12} /> Modifica</Button>
            <Button size="sm" variant="ghost" onClick={deactivate} data-testid="deactivate-managed-plan-btn" className="rounded-full gap-1 text-destructive hover:text-destructive"><Trash size={12} /> Disattiva</Button>
          </div>
        </div>
      )}

      {/* Activate dialog */}
      <Dialog open={activateOpen} onOpenChange={setActivateOpen}>
        <DialogContent data-testid="activate-managed-plan-dialog">
          <DialogHeader>
            <DialogTitle>Attiva Assistenza IT Gestita</DialogTitle>
            <DialogDescription>Scegli il piano e il numero di PC coperti. La fatturazione parte da subito.</DialogDescription>
          </DialogHeader>
          <PlanForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActivateOpen(false)}>Annulla</Button>
            <Button onClick={activate} data-testid="activate-managed-plan-submit" className="rounded-full">Attiva piano</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-managed-plan-dialog">
          <DialogHeader>
            <DialogTitle>Modifica piano</DialogTitle>
            <DialogDescription>
              Cambiare il tipo di piano lo programma dal 1° del mese prossimo.
              Cambiare solo il numero di PC ha effetto immediato.
            </DialogDescription>
          </DialogHeader>
          <PlanForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={saveEdit} data-testid="edit-managed-plan-submit" className="rounded-full">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PlanForm({ form, setForm }) {
  const chosen = PLAN_CATALOG.find(p => p.key === form.plan) || PLAN_CATALOG[0];
  const pc = parseInt(form.pc_count, 10) || 0;
  const total_minutes = pc * chosen.minutes;
  const total_price = pc * chosen.price;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        {PLAN_CATALOG.map(p => (
          <button
            key={p.key}
            type="button"
            onClick={() => setForm({ ...form, plan: p.key })}
            data-testid={`plan-choice-${p.key}`}
            className={`p-3 rounded-md border text-left ${form.plan === p.key ? "border-primary bg-primary/10" : "border-border hover:bg-secondary"}`}
            style={{ transition: "background-color 0.15s ease, border-color 0.15s ease" }}
          >
            <div className="font-display font-bold">{p.label}</div>
            <div className="text-xs text-muted-foreground mt-1">€ {p.price}/PC</div>
            <div className="text-xs text-muted-foreground">{p.minutes} min/PC</div>
          </button>
        ))}
      </div>
      <div>
        <Label className="font-mono text-[10px] uppercase">Numero PC coperti</Label>
        <Input
          type="number"
          min={1}
          value={form.pc_count}
          onChange={(e) => setForm({ ...form, pc_count: e.target.value })}
          data-testid="plan-pc-count-input"
        />
      </div>
      <div className="text-sm bg-secondary/40 rounded-md p-3 border border-border/50">
        <div className="flex justify-between font-mono text-xs">
          <span className="text-muted-foreground">Minuti mensili totali</span>
          <span className="font-bold">{total_minutes} min</span>
        </div>
        <div className="flex justify-between font-mono text-xs mt-1">
          <span className="text-muted-foreground">Costo mensile</span>
          <span className="font-bold">{eur(total_price)} + IVA</span>
        </div>
      </div>
    </div>
  );
}
